export interface DirectoryTenant {
  id: string;
  name: string;
}

export interface DirectoryBusiness {
  id: string;
  tenant_id: string;
  name: string | null;
  address: string | null;
  city: string | null;
  provincia: string | null;
}

export interface BusinessDirectoryEntry {
  id: string;
  tenantId: string;
  businessId: string | null;
  tenantName: string;
  businessName: string;
  address: string | null;
  city: string | null;
  provincia: string | null;
  zoneKey: string;
  zoneLabel: string;
}

export interface BusinessDirectoryZone {
  key: string;
  label: string;
  count: number;
}

export const UNASSIGNED_ZONE_KEY = '__unassigned__';

export function normalizeDirectoryText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('it')
    .trim();
}

function clean(value: string | null | undefined): string | null {
  const result = value?.trim();
  return result ? result : null;
}

function zoneFor(city: string | null): { key: string; label: string } {
  if (!city) return { key: UNASSIGNED_ZONE_KEY, label: 'Zona non indicata' };
  return { key: normalizeDirectoryText(city), label: city };
}

/**
 * Trasforma tenant + sedi autorizzate in una directory piatta. Un tenant senza
 * sedi resta selezionabile: serve anche a recuperare anagrafiche incomplete.
 */
export function buildBusinessDirectory(
  tenants: readonly DirectoryTenant[],
  businesses: readonly DirectoryBusiness[],
): BusinessDirectoryEntry[] {
  const tenantById = new Map(tenants.map((tenant) => [tenant.id, tenant]));
  const seenBusinessIds = new Set<string>();
  const entries: BusinessDirectoryEntry[] = [];

  for (const business of businesses) {
    const tenant = tenantById.get(business.tenant_id);
    if (!tenant || seenBusinessIds.has(business.id)) continue;
    seenBusinessIds.add(business.id);
    const tenantName = clean(tenant.name) ?? `Attività ${tenant.id.slice(0, 8)}`;
    const businessName = clean(business.name) ?? tenantName;
    const city = clean(business.city);
    const zone = zoneFor(city);
    entries.push({
      id: `${tenant.id}:${business.id}`,
      tenantId: tenant.id,
      businessId: business.id,
      tenantName,
      businessName,
      address: clean(business.address),
      city,
      provincia: clean(business.provincia)?.toLocaleUpperCase('it') ?? null,
      zoneKey: zone.key,
      zoneLabel: zone.label,
    });
  }

  const tenantsWithBusinesses = new Set(entries.map((entry) => entry.tenantId));
  for (const tenant of tenants) {
    if (tenantsWithBusinesses.has(tenant.id)) continue;
    const tenantName = clean(tenant.name) ?? `Attività ${tenant.id.slice(0, 8)}`;
    entries.push({
      id: `${tenant.id}:tenant`,
      tenantId: tenant.id,
      businessId: null,
      tenantName,
      businessName: tenantName,
      address: null,
      city: null,
      provincia: null,
      zoneKey: UNASSIGNED_ZONE_KEY,
      zoneLabel: 'Zona non indicata',
    });
  }

  return entries.sort((left, right) => (
    left.zoneLabel.localeCompare(right.zoneLabel, 'it', { sensitivity: 'base' })
    || left.businessName.localeCompare(right.businessName, 'it', { sensitivity: 'base' })
    || left.tenantName.localeCompare(right.tenantName, 'it', { sensitivity: 'base' })
  ));
}

export function businessDirectoryZones(
  entries: readonly BusinessDirectoryEntry[],
): BusinessDirectoryZone[] {
  const zones = new Map<string, BusinessDirectoryZone>();
  for (const entry of entries) {
    const existing = zones.get(entry.zoneKey);
    if (existing) existing.count += 1;
    else zones.set(entry.zoneKey, { key: entry.zoneKey, label: entry.zoneLabel, count: 1 });
  }
  return [...zones.values()].sort((left, right) => {
    if (left.key === UNASSIGNED_ZONE_KEY) return 1;
    if (right.key === UNASSIGNED_ZONE_KEY) return -1;
    return left.label.localeCompare(right.label, 'it', { sensitivity: 'base' });
  });
}

export function filterBusinessDirectory(
  entries: readonly BusinessDirectoryEntry[],
  query: string,
  zoneKey: string | null,
): BusinessDirectoryEntry[] {
  const terms = normalizeDirectoryText(query).split(/\s+/).filter(Boolean);
  return entries.filter((entry) => {
    if (zoneKey && entry.zoneKey !== zoneKey) return false;
    if (terms.length === 0) return true;
    const haystack = normalizeDirectoryText([
      entry.businessName,
      entry.tenantName,
      entry.address,
      entry.city,
      entry.provincia,
    ].filter(Boolean).join(' '));
    return terms.every((term) => haystack.includes(term));
  });
}

